// We are actually creating a perfectly fine hook to use
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type ApiError } from 'services/api';

import { IsUndefined, OmitUndefined } from 'types';
import { MutationAction, MutationParams, MutationResult, ValidationProps } from './types';
import { Mutations } from './definition';

import { emptyFunction } from 'utils/function';
import { useMutationMessages } from './messages';
import { queryToKeyPrefix } from './invalidateQueries';

type UseParams<
  TargetMutations extends Record<string, Mutations<any>>,
  Realm extends keyof TargetMutations,
  SubRealm extends keyof TargetMutations[Realm],
> = {
  onSuccess?: (
    result: Awaited<ReturnType<MutationAction<TargetMutations, Realm, SubRealm>>>,
    ...execParams: Parameters<MutationAction<TargetMutations, Realm, SubRealm>>
  ) => void;

  onError?: (err: ApiError) => void;

  onMutation?: (
    ...execParams: Parameters<MutationAction<TargetMutations, Realm, SubRealm>>
  ) => void;

  onEnd?: () => void;

  block?: boolean;

  /**
   * We can pass the params we need to execute partially or in full
   */
  params?: IsUndefined<Parameters<MutationAction<TargetMutations, Realm, SubRealm>>[0]> extends true
    ? never
    : Partial<Parameters<MutationAction<TargetMutations, Realm, SubRealm>>[0]>;
};

type AreParamsComplete<
  TargetMutations extends Record<string, Mutations<any>>,
  Realm extends keyof TargetMutations,
  Sub extends keyof TargetMutations[Realm],
  Params extends UseParams<TargetMutations, Realm, Sub>,
> = [
  Exclude<keyof OmitUndefined<MutationParams<TargetMutations, Realm, Sub>>, keyof Params['params']>,
] extends [never]
  ? true
  : false;

type ExtraParams<
  TargetMutations extends Record<string, Mutations<any>>,
  Realm extends keyof TargetMutations,
  Sub extends keyof TargetMutations[Realm],
  Params extends UseParams<TargetMutations, Realm, Sub>,
> =
  AreParamsComplete<TargetMutations, Realm, Sub, Params> extends true
    ? {
        onClick: () => Promise<MutationResult<TargetMutations, Realm, Sub>>;

        buttonProps: {
          onClick: () => Promise<MutationResult<TargetMutations, Realm, Sub>>;
          loading: boolean;
        };
      }
    : {
        buttonProps: {
          loading: boolean;
        };
      };

type Actions<TargetMutations extends Record<string, Mutations<any>>> = {
  [Realm in keyof TargetMutations]: {
    [SubRealm in keyof TargetMutations[Realm]]: {
      use<Params extends UseParams<TargetMutations, Realm, SubRealm>>(
        params?: Params,
      ): ValidationProps<TargetMutations, Realm, SubRealm> &
        ExtraParams<TargetMutations, Realm, SubRealm, Params> & {
          loading: boolean;
          error: boolean;

          execute: (
            ...execParams: IsUndefined<
              MutationParams<TargetMutations, Realm, SubRealm>
            > extends true
              ? []
              : AreParamsComplete<TargetMutations, Realm, SubRealm, Params> extends true
                ? [Partial<MutationParams<TargetMutations, Realm, SubRealm>>?]
                : [
                    Pick<
                      MutationParams<TargetMutations, Realm, SubRealm>,
                      Exclude<
                        keyof MutationParams<TargetMutations, Realm, SubRealm>,
                        keyof Params['params']
                      >
                    > &
                      Partial<Params['params']>,
                  ]
          ) => ReturnType<MutationAction<TargetMutations, Realm, SubRealm>>;
        };
    };
  };
};

function buildSingleRealm<
  TargetMutations extends Record<string, Mutations<any>>,
  Realm extends keyof TargetMutations,
  Sub extends keyof TargetMutations[Realm],
>(config: TargetMutations[Realm][Sub]): Actions<TargetMutations>[Realm][Sub] {
  return {
    use({
      onSuccess,
      onError,
      onMutation,
      onEnd,
      block,
      params: execParams,
    }: UseParams<TargetMutations, Realm, Sub> = {}) {
      const queryClient = useQueryClient();

      const { showError, showSuccess } = useMutationMessages();

      const { isPending, isError, mutateAsync, reset } = useMutation({
        mutationFn: config.action,

        onSuccess: async (result: any, ...params: any) => {
          showSuccess(config.messages);

          onSuccess?.(
            result,
            ...(params as Parameters<MutationAction<TargetMutations, Realm, Sub>>),
          );

          if (!config.invalidateOnSuccess?.length) return;

          config.invalidateOnSuccess.forEach((queryRef) => {
            queryClient.invalidateQueries({
              queryKey: queryToKeyPrefix(queryRef),
            });
          });
        },

        onError: (error: ApiError) => {
          showError(error, {
            config: config.messages,
            customParser: config.parseError,
          });

          onError?.(error);
        },

        onMutate: onMutation,

        onSettled: onEnd,
      } as any);

      // Out TS type ensures the app does not see this if no prop is passed or partial
      // is passed
      const buttonProps =
        block || !execParams
          ? {
              loading: isPending,
            }
          : {
              loading: isPending,
              onClick: () => mutateAsync(execParams as any),
            };

      return {
        ...buttonProps,
        error: isError,

        buttonProps,

        execute: block
          ? emptyFunction
          : async (params: any) => mutateAsync({ ...execParams, ...params }),

        reset,
      };
    },
  } as any;
}

function buildSubRealm<
  TargetMutations extends Record<string, Mutations<any>>,
  Realm extends keyof TargetMutations,
>(subRealm: TargetMutations[Realm]): Actions<TargetMutations>[Realm] {
  return Object.fromEntries(
    Object.entries(subRealm).map(([subRealmKey, subRealmConfig]) => [
      subRealmKey,
      buildSingleRealm<TargetMutations, Realm, keyof TargetMutations[Realm]>(subRealmConfig as any),
    ]),
  ) as unknown as Actions<TargetMutations>[Realm];
}

/**
 *
 * @param mutations You app/module mutations
 * @returns An object with the exact same structure of the mutations passed in, with an extra level deep `use()`
 * It will be used as a useMutation hook, but with the ease of being precise to what action you need
 *
 * Example of usage
 *
 * const { loading, execute, ...params } = obj.auth.initiateAuth.use() // params needed, if any
 */
export function createMutationHookObject<TargetMutations extends Record<string, Mutations<any>>>(
  mutations: TargetMutations,
): Actions<TargetMutations> {
  return Object.fromEntries(
    Object.entries(mutations).map(([realmKey, realm]) => [realmKey, buildSubRealm(realm as any)]),
  ) as Actions<TargetMutations>;
}
